import { t } from '../../i18n';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { useLocalizedPortfolio as usePortfolio } from '../../hooks/useLocalizedContent';
import { getImageUrl } from '../../utils/imageUtils';

const WorkSection = () => {
  const { projects } = usePortfolio();
  const featuredProjects = projects.filter(p => p.featured).slice(0, 4);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 1900);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="mb-20">
      {/* Section title */}
      <h2 className="section-title">
        <span>{t("Selected Work")}</span>
      </h2>

      <div className="space-y-10 xl:grid xl:grid-cols-2 xl:gap-x-8 xl:gap-y-12 xl:space-y-0 2xl:gap-x-10">
        {featuredProjects.map((project, index) => (
          <Link
            key={project.id}
            to={`/portfolio/${project.slug}`}
            className={`group cursor-pointer transition-all duration-700 ease-out-expo ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: `${index * 150}ms` }}
          >
            {/* Image container with effects */}
            <div className="relative aspect-video overflow-hidden rounded-lg mb-5 bg-card">
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Corner accent */}
              <div className="absolute top-0 right-0 w-0 h-0 z-10 opacity-0 group-hover:opacity-100 transition-all duration-500"
                style={{
                  borderStyle: 'solid',
                  borderWidth: '0 50px 50px 0',
                  borderColor: 'transparent rgb(var(--color-primary) / 0.3) transparent transparent',
                }}
              />

              {/* Image */}
              <img
                src={getImageUrl(project.image)}
                alt={project.title}
                className="w-full h-full object-cover transition-all duration-700 ease-out-expo group-hover:scale-105 img-grayscale"
              />

              {/* View project indicator */}
              <div className="absolute bottom-4 left-4 z-20 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0">
                <span className="flex items-center gap-2 text-xs uppercase tracking-widest text-foreground font-heading bg-background/80 backdrop-blur-sm px-3 py-2 rounded">
                  {t("View Project")}
                  <ArrowUpRight className="w-3 h-3" />
                </span>
              </div>
            </div>

            {/* Info */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-heading font-semibold text-foreground group-hover:text-primary transition-colors duration-300">
                  {project.title}
                </h3>
                <p className="text-sm text-muted mt-1">
                  {project.technologies.slice(0, 3).join(' / ')}
                </p>
              </div>

              {/* Arrow */}
              <span className="text-muted group-hover:text-primary transition-all duration-300 transform group-hover:translate-x-1 group-hover:-translate-y-1">
                <ArrowUpRight className="w-5 h-5" />
              </span>
            </div>

            {/* Separator line */}
            {index < featuredProjects.length - 1 && (
              <div className="mt-10 h-px w-full bg-gradient-to-r from-foreground/5 via-foreground/5 to-transparent xl:hidden" />
            )}
          </Link>
        ))}
      </div>

      {/* View all link */}
      <Link
        to="/portfolio"
        className={`inline-flex items-center gap-2 mt-12 text-sm text-muted hover:text-primary transition-all duration-300 group ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        style={{ transitionDelay: '800ms' }}
      >
        <span className="font-heading tracking-wide relative">
          {t("View all projects")}
          <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-primary transition-all duration-300 group-hover:w-full" />
        </span>
        <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </Link>
    </section>
  );
};

export default WorkSection;
